/**
 * SearchPage
 *
 * Full-page Algolia search across forms and records, mounted on the `/search`
 * route. `q` and `type` in the URL are the source of truth for the query and the
 * collection filter, so a reload or a shared link restores the same results. The
 * page uses no FlexPage; Esc, the back chevron and the close button all exit to
 * the previous route, or to `/` when there is no history.
 */
import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, Button, Divider, Empty, Typography, Segmented, theme } from 'antd';
import FlexCol from '../components/atoms/FlexCol';
import Icon from '../components/atoms/Icon';
import { SearchInput } from '../components/atoms/SearchInput';
import { SearchResultCard } from '../components/atoms/SearchResultCard';
import FlexBox from '../components/atoms/FlexBox';
import { useSearch, CollectionFilter } from '../use/useSearch';
import { SearchResultItem, normalizeSearchResult } from '../services/searchConfig';

const { Content } = Layout;

const COLLECTION_FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Forms', value: 'formVersions' },
  { label: 'Records', value: 'records' },
];

const COLLECTION_FILTER_LABELS: Record<CollectionFilter, string> = {
  all: 'All search results',
  records: 'Records only',
  formVersions: 'Forms only',
};

export default function SearchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();
  const [searchValue, setSearchValue] = useState('');
  const [collectionFilter, setCollectionFilter] = useState<CollectionFilter>('all');

  const { searchResults, setSearchQuery } = useSearch({ collectionFilter });

  const handleExit = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const q = searchParams.get('q');
    const type = searchParams.get('type') as CollectionFilter | null;

    if (q) {
      setSearchValue(q);
      setSearchQuery(q);
    }
    if (type && ['all', 'records', 'formVersions'].includes(type)) {
      setCollectionFilter(type);
    }
  }, [location.search, setSearchQuery]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape')
        handleExit();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleExit]);

  const updateUrl = (query: string, type: CollectionFilter) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (type !== 'all') params.set('type', type);
    navigate(`/search?${params.toString()}`, { replace: true });
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setSearchQuery(value);
    updateUrl(value, collectionFilter);
  };

  const handleCollectionFilterChange = (value: CollectionFilter) => {
    setCollectionFilter(value);
    updateUrl(searchValue, value);
  };

  return (
    <Layout style={{
      minHeight: '100%',
      display: 'flex',
    }}>
      <Content style={{ padding: token.paddingMD }}>
        <FlexBox
          stretch
          noGrow
          alignStart
          gap={5}
        >
          <Button
            type='text'
            icon={<Icon.ChevronLeft />}
            onClick={handleExit}
            aria-label="Go back"
          />
          <FlexBox column gap={0} style={{ minWidth: 0 }}>
            <SearchInput
              autoFocus
              placeholder="Search anything..."
              value={searchValue}
              onChange={handleSearch}
            />
            <FlexBox stretch>
              <Typography.Text>
                <small>{COLLECTION_FILTER_LABELS[collectionFilter]}</small>
              </Typography.Text>
              <Typography.Text type='secondary'>
                <small>ESC to exit</small>
              </Typography.Text>
            </FlexBox>
          </FlexBox>
          <Button
            type='text'
            icon={<Icon.X />}
            onClick={handleExit}
            aria-label="Close search"
          />
        </FlexBox>

        <FlexBox noGrow style={{ marginTop: 10 }}>
          <Segmented
            value={collectionFilter}
            onChange={(value) => handleCollectionFilterChange(value as CollectionFilter)}
            options={COLLECTION_FILTER_OPTIONS}
            aria-label="Filter by collection type"
          />
        </FlexBox>

        <Divider style={{
          marginTop: 10,
          marginBottom: 15,
        }} />
        {searchValue ?
          (
            searchResults.length > 0 ? (
              <FlexCol gap={0}>
                {searchResults.map(normalizeSearchResult).map((item: SearchResultItem, index: number) => (
                  <div key={index} style={{
                    padding: `${token.paddingSM}px 0`,
                    borderBottom: `${token.lineWidth}px ${token.lineType} ${token.colorSplit}`,
                  }}>
                    <SearchResultCard {...item} />
                  </div>
                ))}
              </FlexCol>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No results found"
              />
            )
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Type something to search..."
            />
          )}
      </Content>
    </Layout>
  );
};